import { useEffect, useReducer, useState, type RefObject } from 'react'

import type { DirectoryRelease } from '@/lib/directory/aggregate-releases'
import { unpackReleases, type PackedDirectory } from '@/lib/directory/pack-releases'

type CatalogState =
  | { href: string; status: 'loading' | 'error' }
  | { href: string; status: 'ready'; releases: DirectoryRelease[] }

const useDirectoryInView = (ref: RefObject<HTMLDivElement | null>, enabled: boolean, requested: boolean) => {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (!enabled || visible || !ref.current) return
    if (requested || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '400px 0px' }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled, visible, ref, requested])
  return visible
}

const loadCatalog = async (href: string, signal: AbortSignal) => {
  const response = await fetch(href, { signal })
  if (!response.ok) throw new Error('Directory request failed')
  const packed: PackedDirectory = await response.json()
  return unpackReleases(packed)
}

// Keep the real server-rendered rows while loading. Interaction also starts
// the request, so anchors, shared filters and stored selections do not have
// to wait for an intersection callback.
export const useDeferredCatalog = (
  ref: RefObject<HTMLDivElement | null>,
  href: string | undefined,
  requested: boolean
) => {
  const visible = useDirectoryInView(ref, !!href, requested)
  const shouldLoad = visible || requested
  const [attempt, retry] = useReducer((value: number) => value + 1, 0)
  const [catalog, setCatalog] = useState<CatalogState>()
  useEffect(() => {
    if (!href || !shouldLoad) return
    const controller = new AbortController()
    setCatalog({ href, status: 'loading' })
    void loadCatalog(href, controller.signal).then(
      (releases) => {
        if (!controller.signal.aborted) setCatalog({ href, status: 'ready', releases })
      },
      () => {
        if (!controller.signal.aborted) setCatalog({ href, status: 'error' })
      }
    )
    return () => controller.abort()
  }, [href, shouldLoad, attempt])
  const current = catalog?.href === href ? catalog : undefined
  return {
    status: current?.status ?? 'idle',
    releases: current?.status === 'ready' ? current.releases : undefined,
    retry,
  }
}
